import { useGetDailyApplicantsTrendsQuery } from '@/app/features/recruitment/dashboard/recruitment-dashboard.api';
import EmptyState from '@/components/EmptyState';
import { ListIcon } from '@/icons';
import { Flex, Skeleton } from 'antd';
import dayjs from 'dayjs';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

// ── Tooltip ──────────────────────────────────────────────────────────────────

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;
  if (!active || !payload?.length) return null;

  const hired = payload.find((p: any) => p.dataKey === 'hired')?.value ?? 0;
  const shortlisted =
    payload.find((p: any) => p.dataKey === 'shortlisted')?.value ?? 0;

  return (
    <div className="bg-[#0F172A] text-white p-4 rounded-xl shadow-xl border border-[#1f2d40]">
      <p className="text-center text-white text-base font-normal mb-2">
        {String(label)}
      </p>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2 text-sm">
          <span className="w-3 h-3 rounded-full bg-[#2CA2C1]" />
          Hired: <span className="font-semibold">{hired}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-3 h-3 rounded-full bg-[#FFC300]" />
          Shortlisted: <span className="font-semibold">{shortlisted}</span>
        </div>
      </div>
    </div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  startDate: string;
  endDate: string;
}

export default function ApplicationTrends({ startDate, endDate }: Props) {
  const { data, isLoading, isFetching } = useGetDailyApplicantsTrendsQuery(
    { start_date: startDate, end_date: endDate },
    { skip: !startDate || !endDate },
  );

  const loading = isLoading || isFetching;
  const points = data?.data?.points ?? [];

  const chartData = points.map((point) => ({
    name: dayjs(point.date).format('DD MMM'),
    hired: point.hired,
    shortlisted: point.shortlisted,
  }));

  const isEmpty = !loading && chartData.length === 0;

  return (
    <div className="text-white p-5 rounded-xl shadow-xl bg-theme-slate-800 border border-theme-gray-700 h-full">
      {/* Header */}
      <Flex className="mb-4" align="center" justify="space-between">
        <div className="text-lg md:text-2xl font-bold">Application Trends</div>
        <Flex align="center" gap={12} className="text-sm text-white">
          <Flex align="center" gap={6}>
            <span className="w-4 h-4 rounded-md bg-[#2CA2C1]" />
            Hired
          </Flex>
          <Flex align="center" gap={6}>
            <span className="w-4 h-4 rounded-md bg-[#FFC300]" />
            Shortlisted
          </Flex>
        </Flex>
      </Flex>

      {/* Body */}
      <div className="flex items-center justify-center w-full">
        {loading ? (
          <Skeleton.Node active className="!w-full !h-[290px] !rounded-xl" />
        ) : isEmpty ? (
          <EmptyState
            title="No Data Found"
            description="No application data available for the last 6 months."
            icon={<ListIcon size={40} />}
          />
        ) : (
          <ResponsiveContainer width="100%" height={290}>
            <AreaChart
              data={chartData}
              margin={{ top: 20, right: 0, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorHired" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#21495f" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#1c334b" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="colorShortlisted"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#6d5a2b" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#252b3d" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#1e2a38" />
              <XAxis dataKey="name" stroke="#ffffff" tick={{ fontSize: 12 }} />
              <YAxis
                stroke="#ffffff"
                tick={{ fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ stroke: '#d1d5db', strokeWidth: 1 }}
              />

              <Area
                type="monotone"
                dataKey="hired"
                stroke="#2CA2C1"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorHired)"
              />
              <Area
                type="monotone"
                dataKey="shortlisted"
                stroke="#FFC300"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorShortlisted)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
